export interface EquipmentItem {
  slug: string;
  name: string;
  stage: 'Screening' | 'Separation' | 'Clarification' | 'Aeration' | 'Mixing' | 'Filtration' | 'Spares';
  summary: string;
  body: string[];
  specs: Array<{ label: string; value: string }>;
  image: string;
}

/**
 * The 15 lines that were dead <button> items in the old Services dropdown.
 * Each one is now a real, indexable, linkable route.
 */
export const EQUIPMENT: EquipmentItem[] = [
  {
    slug: 'oil-skimmer',
    name: 'Oil Skimmers',
    stage: 'Separation',
    summary: 'Belt, disc and tube skimmers for removing floating oil before it reaches treatment.',
    body: [
      'Floating oil that reaches a biological stage kills it. An oil skimmer lifts free oil off the surface continuously, upstream of everything else.',
      'We build three types in-house and size them to your tank geometry rather than to a catalogue number.'
    ],
    specs: [
      { label: 'Types', value: 'Belt, disc, tube' },
      { label: 'Material', value: 'SS 304 / SS 316 / MS with coating' },
      { label: 'Drive', value: 'Geared motor, 0.25–1.5 kW' },
      { label: 'Scope', value: 'Supply, installation, commissioning' }
    ],
    image: '/assets/images/waste-water-treatment-plant.jpg'
  },
  {
    slug: 'cpi-separator',
    name: 'CPI Separator',
    stage: 'Separation',
    summary: 'Corrugated plate interceptor for oil–water separation at source.',
    body: ['Plate-pack separation for oily effluent, sized to your flow and oil droplet size.'],
    specs: [
      { label: 'Principle', value: 'Corrugated plate interception' },
      { label: 'Material', value: 'SS / FRP plate packs' },
      { label: 'Typical use', value: 'Refinery, workshop and garage effluent' }
    ],
    image: '/assets/images/grey-water-treatment-plants1.jpg'
  },
  {
    slug: 'dissolved-air-flotation',
    name: 'Dissolved Air Flotation (DAF)',
    stage: 'Separation',
    summary: 'Fine solids and emulsified oil floated out with micro-bubbles.',
    body: ['DAF handles what gravity cannot: emulsified oil, fine suspended solids and low-density flocs.'],
    specs: [
      { label: 'Capacity', value: '5–200 m³/hr' },
      { label: 'Recycle ratio', value: '10–30%' },
      { label: 'Options', value: 'Integrated flocculation tube, sludge scraper' }
    ],
    image: '/assets/images/water-treatment-plant-process-FEATURE-compressed.jpg'
  },
  {
    slug: 'lamella-clarifier',
    name: 'Lamella Clarifier',
    stage: 'Clarification',
    summary: 'High settling area in a small footprint.',
    body: ['Inclined plate settling gives you the effective area of a much larger clarifier in a fraction of the floor space.'],
    specs: [
      { label: 'Plate angle', value: '55–60°' },
      { label: 'Material', value: 'SS 304 / PP plates' },
      { label: 'Footprint', value: '~1/5 of an equivalent circular clarifier' }
    ],
    image: '/assets/images/grey-water-treatment-plants1.jpg'
  },
  {
    slug: 'clarifier',
    name: 'Clarifier & HRSCC',
    stage: 'Clarification',
    summary: 'Circular, scraper-driven clarifiers and high-rate solid contact clarifiers.',
    body: ['Primary and secondary clarification with central drive, bridge-mounted scraper and sludge draw-off.'],
    specs: [
      { label: 'Diameter', value: '3–20 m' },
      { label: 'Drive', value: 'Central or peripheral' },
      { label: 'Construction', value: 'Civil tank with mechanicals, or fully fabricated' }
    ],
    image: '/assets/images/waste-water-treatment-plant.jpg'
  },
  {
    slug: 'surface-aerator',
    name: 'Surface Aerators',
    stage: 'Aeration',
    summary: 'Fixed and high-speed floating aerators for aeration tanks and lagoons.',
    body: ['Oxygen transfer for activated sludge, aerated lagoons and equalisation tanks — fixed bridge-mounted or floating.'],
    specs: [
      { label: 'Types', value: 'Fixed, high-speed floating' },
      { label: 'Motor', value: '3–75 kW' },
      { label: 'Transfer rate', value: '1.8–2.2 kg O₂/kWh' }
    ],
    image: '/assets/images/dws-landustries-landy-adu-dhabi-750px.jpg'
  },
  {
    slug: 'static-mixer',
    name: 'Static Mixers',
    stage: 'Mixing',
    summary: 'Inline mixers, SS and jacketed, for chemical dosing lines.',
    body: ['No moving parts, no power: fixed helical elements mix dosed chemical into the stream within a few pipe diameters.'],
    specs: [
      { label: 'Line size', value: 'DN25–DN600' },
      { label: 'Material', value: 'SS 304 / SS 316, jacketed options' },
      { label: 'Use', value: 'Coagulant, polymer and pH dosing' }
    ],
    image: '/assets/images/water-treatment-plant-process-FEATURE-compressed.jpg'
  },
  {
    slug: 'agitator',
    name: 'Agitators',
    stage: 'Mixing',
    summary: 'Chemical mixing and flash mixing agitators.',
    body: ['Top-entry agitators for dosing tanks, flash mixers and flocculation basins.'],
    specs: [
      { label: 'Impeller', value: 'Propeller, pitched blade, flat blade' },
      { label: 'Motor', value: '0.5–15 kW with gearbox' },
      { label: 'Material', value: 'SS 304 / SS 316 / rubber lined' }
    ],
    image: '/assets/images/grey-water-treatment-plants1.jpg'
  },
  {
    slug: 'pressure-sand-carbon-filter',
    name: 'PSF, ACF & DMF',
    stage: 'Filtration',
    summary: 'Pressure sand, activated carbon and dual media filters.',
    body: ['Tertiary polishing ahead of reuse, UF or RO — turbidity, colour and residual organics.'],
    specs: [
      { label: 'Vessel', value: 'MSEP / FRP' },
      { label: 'Flow', value: '1–100 m³/hr' },
      { label: 'Backwash', value: 'Manual or automatic multiport' }
    ],
    image: '/assets/images/water-treatment-plant-process-FEATURE-compressed.jpg'
  },
  {
    slug: 'self-cleaning-filter',
    name: 'Self-Cleaning Filter',
    stage: 'Filtration',
    summary: 'Continuous duty filtration with no downtime for cleaning.',
    body: ['Automatic backwash on differential pressure keeps the line in service through the cleaning cycle.'],
    specs: [
      { label: 'Screen', value: '25–500 micron' },
      { label: 'Control', value: 'Differential pressure or timer' },
      { label: 'Material', value: 'SS 304 / SS 316' }
    ],
    image: '/assets/images/waste-water-treatment-plant.jpg'
  },
  {
    slug: 'mechanical-bar-screen',
    name: 'Mechanical Bar Screen',
    stage: 'Screening',
    summary: 'Raked bar screens at the plant inlet.',
    body: ['The first line of defence: rags, plastics and grit removed mechanically before any pump or tank.'],
    specs: [
      { label: 'Bar spacing', value: '6–25 mm' },
      { label: 'Rake', value: 'Chain or cable driven' },
      { label: 'Material', value: 'SS 304 / MS galvanised' }
    ],
    image: '/assets/images/dws-landustries-landy-adu-dhabi-750px.jpg'
  },
  {
    slug: 'oil-grease-interceptor',
    name: 'Oil & Grease Interceptor',
    stage: 'Separation',
    summary: 'Kitchen and workshop grease interception at source.',
    body: ['Passive interception close to the source keeps grease out of the drainage network and the plant.'],
    specs: [
      { label: 'Capacity', value: '50–5000 litres' },
      { label: 'Material', value: 'SS 304 / FRP' },
      { label: 'Cleaning', value: 'Removable baskets and baffles' }
    ],
    image: '/assets/images/grey-water-treatment-plants1.jpg'
  },
  {
    slug: 'dosing-system',
    name: 'Dosing System',
    stage: 'Mixing',
    summary: 'Skid-mounted chemical dosing with tank, pump and mixer.',
    body: ['Complete dosing skids for coagulant, polymer, acid, alkali and hypo.'],
    specs: [
      { label: 'Pump', value: 'Diaphragm metering, 1–200 LPH' },
      { label: 'Tank', value: 'HDPE / FRP with agitator' },
      { label: 'Control', value: 'Manual, flow-proportional or pH-based' }
    ],
    image: '/assets/images/water-treatment-plant-process-FEATURE-compressed.jpg'
  },
  {
    slug: 'sewage-treatment-plant',
    name: 'Sewage Treatment Plant',
    stage: 'Aeration',
    summary: 'MBBR, MBR, SBR and conventional STPs from 10 to 500 KLD.',
    body: [
      'Packaged and civil-tank sewage treatment plants, designed to the discharge or reuse standard you have to meet.',
      'Design, fabrication, supply, installation and commissioning are all ours — there is one party responsible for the outcome.'
    ],
    specs: [
      { label: 'Technologies', value: 'MBBR, MBR, SBR, conventional ASP' },
      { label: 'Capacity', value: '10–500 KLD' },
      { label: 'Output', value: 'Discharge or reuse standard, as specified' },
      { label: 'Scope', value: 'Turnkey, including commissioning' }
    ],
    image: '/assets/images/waste-water-treatment-plant.jpg'
  },
  {
    slug: 'effluent-treatment-plant',
    name: 'Effluent Treatment Plant',
    stage: 'Separation',
    summary: 'Industry-specific ETPs built around your actual effluent characteristics.',
    body: [
      'No two effluents are the same. We design the train around your analysis — not a template — and build the units ourselves.'
    ],
    specs: [
      { label: 'Typical train', value: 'Screening → equalisation → DAF → biological → tertiary' },
      { label: 'Capacity', value: '5–500 KLD' },
      { label: 'Scope', value: 'Turnkey, including commissioning' }
    ],
    image: '/assets/images/water-treatment-plant-process-FEATURE-compressed.jpg'
  }
];

export const STAGES: Array<{ key: string; title: string; blurb: string }> = [
  { key: 'Screening',     title: 'Screening & intake',  blurb: 'Mechanical bar screens and oil skimmers take out what should never reach a tank.' },
  { key: 'Separation',    title: 'Separation',          blurb: 'DAF, CPI separators and interceptors pull out oil, grease and solids.' },
  { key: 'Clarification', title: 'Clarification',       blurb: 'Lamella and circular clarifiers settle what separation leaves behind.' },
  { key: 'Aeration',      title: 'Biological',          blurb: 'MBBR, MBR, SBR and conventional trains, aerated by our own aerators.' },
  { key: 'Filtration',    title: 'Polishing & reuse',   blurb: 'Sand and carbon filters, UF and RO — water good enough to put back to work.' }
];

export function findEquipment(slug: string): EquipmentItem | undefined {
  return EQUIPMENT.find(e => e.slug === slug);
}
